import { useEffect, useRef, useState, useCallback } from 'react';
import { BACKEND_URL } from "../config";

const API_URL = BACKEND_URL;

/**
 * SSE (Server-Sent Events) Hook - Anlık güncellemeler için
 * 400-500 kullanıcı için optimize edilmiş
 */
export const useSSE = (personnelId, onMessage) => {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const eventSourceRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);

  const connect = useCallback(() => {
    if (!personnelId) return;
    
    // Mevcut bağlantıyı kapat
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    try {
      const sseUrl = `${API_URL}/api/sse/${personnelId}`;
      console.log('SSE bağlanıyor:', sseUrl);
      
      eventSourceRef.current = new EventSource(sseUrl);

      eventSourceRef.current.onopen = () => {
        console.log('SSE bağlandı');
        setIsConnected(true);
        setConnectionError(null);
        reconnectAttempts.current = 0;
      };

      eventSourceRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('SSE mesajı:', data);
          
          if (onMessage && data.type !== 'CONNECTED') {
            onMessage(data);
          }
        } catch (e) {
          console.error('SSE mesaj parse hatası:', e);
        }
      };

      eventSourceRef.current.onerror = (error) => {
        console.error('SSE hatası:', error);
        setIsConnected(false);

        // EventSource kapandıysa yeniden bağlan — sınırsız (exponential backoff, max 30sn).
        // Eski 5-deneme limiti kaldırıldı: bağlantı kalıcı olarak ölünce kullanıcı çıkış-girişe zorlanıyordu.
        if (eventSourceRef.current?.readyState === EventSource.CLOSED) {
          reconnectAttempts.current += 1;
          const delay = Math.min(1000 * Math.pow(2, Math.min(reconnectAttempts.current, 5)), 30000);
          console.log(`SSE yeniden bağlanılıyor... (deneme ${reconnectAttempts.current}, ${delay}ms)`);

          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, delay);
        }
      };
    } catch (error) {
      console.error('SSE bağlantı hatası:', error);
      setConnectionError('Bağlantı başlatılamadı');
    }
  }, [personnelId, onMessage]);

  // Bağlantıyı başlat
  useEffect(() => {
    if (personnelId) {
      connect();
    }

    return () => {
      // Cleanup
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, [personnelId, connect]);

  // Manuel yeniden bağlanma
  const reconnect = useCallback(() => {
    reconnectAttempts.current = 0;
    connect();
  }, [connect]);

  return { isConnected, connectionError, reconnect };
};

// Geriye uyumluluk için useWebSocket'i de export et
export const useWebSocket = useSSE;

export default useSSE;
